import React from "react";
import { Link } from "react-router-dom";
import { Target, Heart, Leaf, Zap, ArrowRight } from "lucide-react";
import { company, images, stats } from "../mock";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

const values = [
  { icon: Target, title: "Reliability first", desc: "We monitor every machine so it's always stocked, clean and ready — no empty shelves, no downtime." },
  { icon: Heart, title: "Genuine service", desc: "Real people who answer the phone and turn up fast. Service is the whole point of what we do." },
  { icon: Leaf, title: "Balanced choices", desc: "From energy drinks to healthy snacks, we curate a mix your team actually wants to reach for." },
  { icon: Zap, title: "Modern by default", desc: "Tap & Go payments, smart monitoring and sleek machines that look right in any space." }
];

export default function About() {
  return (
    <main>
      <PageHero
        eyebrow="About Us"
        title="We built Elite Vending to make vending genuinely good"
        subtitle="Most machines are old, half-empty and forgotten. We do the opposite — premium hardware, real service and zero cost for qualifying workplaces."
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gold/20">
              <img src={images.breakRoom} alt="Modern break room" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl sm:text-4xl text-ink">Quality. Service. Excellence.</h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              Elite Vending started with a simple frustration: workplace vending machines were letting people down. Outdated units, cash-only payments and shelves that stayed empty for weeks.
            </p>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              So we rebuilt the experience from the ground up. Brand-new machines, back-to-base stock monitoring, modern payments and a service team that actually shows up. Today we run machines across Australia, the UK, the USA and Canada — and we install them free for qualifying sites.
            </p>
            <Link to="/contact">
              <Button className="mt-7 h-12 px-7 rounded-full bg-gold hover:bg-gold-dark text-ink font-semibold group">
                Talk to us <ArrowRight size={17} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl gold-text">{s.value}{s.suffix}</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-white/60">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gold-soft/40 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-ink">What we stand for</h2>
            <p className="mt-4 text-lg text-muted-foreground">The principles behind every machine we install.</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="h-full rounded-2xl border border-gold/20 bg-white p-7 hover:shadow-xl transition-shadow">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-ink text-gold">
                    <v.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink">{v.title}</h3>
                  <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
